// * Session policy — the pure decision core for client-side auth. No store, no axios, no
//   side effects. Every "what do we do on a 401 / a refresh failure / an expired token"
//   decision lives here (discriminated-union outcomes); the session coordinator performs the
//   effects, the interceptor performs the HTTP mechanics, the store owns the state.
//
//   This is the single home for the collapse taxonomy: collapse ONLY on a definitive auth
//   failure of a session-bearing endpoint — never on credential endpoints, never on a
//   rotation race, never on a transient failure.

import { isAuthFailure } from "@/services/blackfisk/errorTaxonomy";

// * Minimal structural error shape (what the policy needs from EntityServiceResponseError).
type ErrorLike = { code?: string | number | null };

// ---------- Endpoint classification ----------

// * The refresh endpoint: the single-use cookie is rotated here.
export const isRefreshUrl = (url: string): boolean => url.includes("/v2/auth/refresh");

// * Endpoints that carry the server-minted device binding header (refresh + profile) so the
//   backend can bind the session to the device.
export const isDeviceBoundEndpoint = (url: string): boolean =>
  url.includes("/v2/auth/refresh") || url.includes("/v2/auth/profile");

// * Endpoints that OWN the server session: a 401 here means the session is gone (or was
//   rotated by a sibling tab), so local state must be collapsed rather than half-authenticated.
export const isSessionEndpoint = (url: string): boolean =>
  isRefreshUrl(url) ||
  url.includes("/v2/auth/profile") ||
  url.includes("/v2/auth/logout") ||
  url.includes("/v2/blackfisk/user/current");

// * Any auth/identity endpoint (session + credential). Used to decide whether a 401 is a
//   "bad credentials" result (credential) or a generic request that may be refresh-retried.
export const isAuthEndpoint = (url: string): boolean =>
  url.includes("/v2/auth/") ||
  url.includes("/v1/blackfisk/user/") ||
  url.includes("/v2/blackfisk/user/current");

// * Credential endpoints (login/quickLogin/quickVerify): a 401 means BAD CREDENTIALS, not a
//   dead session — they must never collapse or broadcast.
export const isCredentialEndpoint = (url: string): boolean =>
  isAuthEndpoint(url) && !isSessionEndpoint(url);

// ---------- 401 classification ----------

export type UnauthorizedKind = "session-dead" | "session-race" | "credential" | "other";

// * Classify a 401 by the endpoint it hit. `rotationRaceActive` is true when the request is
//   the refresh call itself AND a sibling tab broadcast a token within the adopt window —
//   i.e. the shared cookie was rotated by another tab, which is NOT a dead session.
export const classifyUnauthorized = (ctx: {
  url: string;
  rotationRaceActive: boolean;
}): UnauthorizedKind => {
  if (isSessionEndpoint(ctx.url)) {
    return ctx.rotationRaceActive ? "session-race" : "session-dead";
  }
  if (isAuthEndpoint(ctx.url)) {
    return "credential";
  }
  return "other";
};

// ---------- Refresh gating ----------

export type RefreshGate = "attempt" | "already-retried";

// * A generic (non-auth) request is refresh-retried at most once per signature. `retryKeySeen`
//   means this signature already received a fresh-token retry and 401'd again — refreshing
//   would loop forever, so reject instead.
export const gateRefresh = (ctx: { retryKeySeen: boolean }): RefreshGate =>
  ctx.retryKeySeen ? "already-retried" : "attempt";

// ---------- Refresh failure ----------

export type RefreshFailureDecision =
  | { action: "adopt-retry" }
  | { action: "collapse" }
  | { action: "reject-transient" };

// * Decide what a FAILED refresh means. A sibling broadcast within the adopt window (and no
//   adopt-retry yet) → adopt + retry once. A definitive auth failure (401/403) → collapse.
//   Anything else (429/5xx/network) is transient — never collapse.
export const decideRefreshFailure = (ctx: {
  hasRecentTokenUpdate: boolean;
  adoptRetried: boolean;
  refreshFailedWith?: number | null;
}): RefreshFailureDecision => {
  if (ctx.hasRecentTokenUpdate && !ctx.adoptRetried) {
    return { action: "adopt-retry" };
  }
  if (isAuthFailure(ctx.refreshFailedWith)) {
    return { action: "collapse" };
  }
  return { action: "reject-transient" };
};

// ---------- Proactive ensure (boot / focus) ----------

export type EnsureSessionDecision = "adopt" | "refresh" | "noop";

// * Focus-triggered expiry check: do nothing unless the access token has expired; if a sibling
//   refreshed within the adopt window, adopt (no-op) rather than burning the single-use cookie;
//   otherwise refresh.
export const decideEnsureSession = (ctx: {
  adoptWindowActive: boolean;
  isExpired: boolean;
}): EnsureSessionDecision => {
  if (!ctx.isExpired) {
    return "noop";
  }
  return ctx.adoptWindowActive ? "adopt" : "refresh";
};

// ---------- Boot rehydration (profile) ----------

export type IdentityResolutionDecision =
  | { action: "collapse" }
  | { action: "drop-in-memory-tokens" };

// * The profile never resolved after a successful refresh. A definitive auth failure means the
//   session is truly gone → collapse. A transient exhaustion (429/5xx/network) must NOT wipe
//   the persisted partial or broadcast — drop only the in-memory tokens.
export const decideIdentityResolutionFailure = (ctx: {
  errors: readonly ErrorLike[];
}): IdentityResolutionDecision =>
  ctx.errors.some((e) => isAuthFailure(e.code))
    ? { action: "collapse" }
    : { action: "drop-in-memory-tokens" };

// * Whether to retry resolving the profile: retry while the failure is not a definitive auth
//   failure and attempts remain.
export const shouldRetryIdentityResolution = (ctx: {
  errors: readonly ErrorLike[];
  attempt: number;
  maxAttempts: number;
}): boolean => ctx.attempt < ctx.maxAttempts - 1 && !ctx.errors.some((e) => isAuthFailure(e.code));
